// @ts-nocheck
import { useState, useEffect } from 'react';
import {
  X,
  Key,
  Shield,
  Copy,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Plus,
  Settings
} from 'lucide-react';
import { AgentRegistrationService } from '../../services/api/AgentRegistrationService';
import { tone, toneForStatus } from "components/ui/tone";
import { clickable } from "components/ui/interactive";

const APIKeysModal = ({ onClose, onAgentRegistered }) => {
  const [activeTab, setActiveTab] = useState('register');
  const [registrationForm, setRegistrationForm] = useState({
    name: '',
    description: '',
    owner: '',
    authType: 'api_key',
    permissions: []
  });
  const [showCredentials, setShowCredentials] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedField, setCopiedField] = useState('');
  const [existingAgents, setExistingAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);


  const registrationService = new AgentRegistrationService();

  // Load existing agents when modal opens
  useEffect(() => {
    loadExistingAgents();
  }, []);

  const loadExistingAgents = async () => {
    try {
      const agents = await registrationService.listAgents();
      // Add mock agents for demo purposes
      const mockAgents = [
        {
          id: 'agent_demo_1',
          name: 'Marketing Automation Bot',
          description: 'Handles automated marketing campaigns and lead nurturing',
          owner: 'marketing@company.com',
          status: 'active',
          credentials: {
            type: 'api_key',
            credentials: {
              apiKey: 'ak_demo_1234567890abcdef1234567890abcdef12345678',
              keyLocation: 'header',
              keyName: 'X-API-Key'
            }
          },
          permissions: [
            { resource: 'campaigns', actions: ['create', 'read', 'update'] },
            { resource: 'leads', actions: ['read', 'update'] }
          ],
          usage: {
            totalRequests: 1247,
            quotaUsed: 1247,
            quotaLimit: 10000,
            lastRequest: new Date(Date.now() - 1000 * 60 * 15)
          },
          audit: {
            createdAt: new Date('2024-01-15'),
            createdBy: 'marketing@company.com'
          }
        },
        {
          id: 'agent_demo_2',
          name: 'Lead Scoring Agent',
          description: 'AI-powered lead scoring and qualification system',
          owner: 'sales@company.com',
          status: 'active',
          credentials: {
            type: 'oauth',
            credentials: {
              clientId: 'agent_oauth_client_abc123',
              clientSecret: 'oauth_secret_xyz789def456ghi123jkl456',
              accessToken: 'access_token_current_session_12345',
              tokenType: 'Bearer'
            }
          },
          permissions: [
            { resource: 'leads', actions: ['read', 'update'] },
            { resource: 'analytics', actions: ['read'] }
          ],
          usage: {
            totalRequests: 892,
            quotaUsed: 892,
            quotaLimit: 5000,
            lastRequest: new Date(Date.now() - 1000 * 60 * 8)
          },
          audit: {
            createdAt: new Date('2024-02-01'),
            createdBy: 'sales@company.com'
          }
        },
        {
          id: 'agent_demo_3',
          name: 'Webhook Processor',
          description: 'Processes incoming webhooks from external systems',
          owner: 'dev@company.com',
          status: 'suspended',
          credentials: {
            type: 'webhook_signature',
            credentials: {
              secret: 'whsec_webhook_secret_validation_key_12345',
              algorithm: 'sha256',
              headerName: 'X-Webhook-Signature'
            }
          },
          permissions: [
            { resource: 'leads', actions: ['create', 'update'] },
            { resource: 'campaigns', actions: ['read'] }
          ],
          usage: {
            totalRequests: 2156,
            quotaUsed: 2156,
            quotaLimit: 15000,
            lastRequest: new Date(Date.now() - 1000 * 60 * 60 * 2)
          },
          audit: {
            createdAt: new Date('2024-01-20'),
            createdBy: 'dev@company.com'
          }
        }
      ];
      setExistingAgents([...agents, ...mockAgents]);
    } catch (error) {
      console.error('Failed to load existing agents:', error);
      setError('Failed to load existing agents');
    }
  };

  const handleViewAgentCredentials = (agent) => {
    setSelectedAgent(agent);
    setActiveTab('credentials');
  };

  const handleRegenerateCredentials = async (agentId) => {
    try {
      setLoading(true);
      const response = await registrationService.regenerateCredentials(agentId);
      if (response.success) {
        setGeneratedCredentials(response.credentials);
        // Refresh the agents list
        await loadExistingAgents();
        setActiveTab('credentials');
      } else {
        setError(response.error || 'Failed to regenerate credentials');
      }
    } catch (error) {
      setError(error.message || 'Failed to regenerate credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendAgent = async (agentId) => {
    try {
      const success = await registrationService.suspendAgent(agentId);
      if (success) {
        await loadExistingAgents();
      } else {
        setError('Failed to suspend agent');
      }
    } catch (error) {
      setError(error.message || 'Failed to suspend agent');
    }
  };

  const handleReactivateAgent = async (agentId) => {
    try {
      const success = await registrationService.reactivateAgent(agentId);
      if (success) {
        await loadExistingAgents();
      } else {
        setError('Failed to reactivate agent');
      }
    } catch (error) {
      setError(error.message || 'Failed to reactivate agent');
    }
  };

  const getStatusColor = (status) => tone(toneForStatus(status)).chip;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'suspended': return <AlertCircle className="w-4 h-4" />;
      case 'revoked': return <X className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatLastUsed = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const availablePermissions = [
    { resource: 'campaigns', label: 'Campaigns', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'leads', label: 'Leads', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'emails', label: 'Emails', actions: ['create', 'read', 'update'] },
    { resource: 'segments', label: 'Segments', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'analytics', label: 'Analytics', actions: ['read'] }
  ];

  const handlePermissionChange = (resource, actions) => {
    const updatedPermissions = registrationForm.permissions.filter(p => p.resource !== resource);
    if (actions.length > 0) {
      updatedPermissions.push({ resource, actions });
    }
    setRegistrationForm({ ...registrationForm, permissions: updatedPermissions });
  };

  const handleRegisterAgent = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await registrationService.registerAgent(registrationForm);
      
      if (response.success) {
        setGeneratedCredentials(response.credentials);
        setActiveTab('credentials');
        
        // Create mock agent for parent component
        const mockAgent = {
          id: response.agentId,
          name: registrationForm.name,
          type: 'Custom Agent',
          status: 'active',
          lastUsed: new Date(),
          requestsToday: 0,
          quotaUsed: 0,
          quotaLimit: 10000
        };
        
        if (onAgentRegistered) {
          onAgentRegistered(mockAgent);
        }
      } else {
        setError(response.error || 'Registration failed');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const isFormValid = () => {
    return registrationForm.name.trim() && 
           registrationForm.description.trim() && 
           registrationForm.owner.trim() && 
           registrationForm.permissions.length > 0;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Key className="w-5 h-5 text-blue-600 dark:text-blue-300" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-content">API Keys & Authentication</h2>
              <p className="text-sm text-content-secondary">Manage AI agent access credentials</p>
            </div>
          </div>
          <button aria-label="Close"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-content-muted" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('register')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'register'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Register Agent
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'manage'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Manage Agents
            </button>
            <button
              onClick={() => setActiveTab('credentials')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'credentials'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              disabled={!generatedCredentials && !selectedAgent}
            >
              View Credentials
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'register' && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-content mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Agent Name *
                    </label>
                    <input
                      type="text"
                      value={registrationForm.name}
                      onChange={(e) => setRegistrationForm({ ...registrationForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Marketing Automation Bot"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Owner *
                    </label>
                    <input
                      type="text"
                      value={registrationForm.owner}
                      onChange={(e) => setRegistrationForm({ ...registrationForm, owner: e.target.value })}
                      className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., john@company.com"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={registrationForm.description}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe what this agent will be used for..."
                  />
                </div>
              </div>

              {/* Authentication Type */}
              <div>
                <h3 className="text-lg font-medium text-content mb-4">Authentication Method</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      registrationForm.authType === 'api_key'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-line hover:border-line-strong'
                    }`}
                    {...clickable(() => setRegistrationForm({ ...registrationForm, authType: 'api_key' }))}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Key className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                      <span className="font-medium">API Key</span>
                    </div>
                    <p className="text-sm text-content-secondary">Simple key-based authentication</p>
                  </div>
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      registrationForm.authType === 'oauth'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-line hover:border-line-strong'
                    }`}
                    {...clickable(() => setRegistrationForm({ ...registrationForm, authType: 'oauth' }))}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Shield className="w-5 h-5 text-green-600 dark:text-green-300" />
                      <span className="font-medium">OAuth 2.0</span>
                    </div>
                    <p className="text-sm text-content-secondary">Secure token-based authentication</p>
                  </div>
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      registrationForm.authType === 'webhook_signature'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-line hover:border-line-strong'
                    }`}
                    {...clickable(() => setRegistrationForm({ ...registrationForm, authType: 'webhook_signature' }))}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Settings className="w-5 h-5 text-purple-600 dark:text-purple-300" />
                      <span className="font-medium">Webhook</span>
                    </div>
                    <p className="text-sm text-content-secondary">Signature-based webhook authentication</p>
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div>
                <h3 className="text-lg font-medium text-content mb-4">Permissions</h3>
                <div className="space-y-4">
                  {availablePermissions.map((permission) => {
                    const selectedPermission = registrationForm.permissions.find(p => p.resource === permission.resource);
                    const selectedActions = selectedPermission?.actions || [];

                    return (
                      <div key={permission.resource} className="border border-line rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-content">{permission.label}</h4>
                          <span className="text-sm text-content-muted">
                            {selectedActions.length} of {permission.actions.length} actions
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {permission.actions.map((action) => (
                            <label key={action} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedActions.includes(action)}
                                onChange={(e) => {
                                  const newActions = e.target.checked
                                    ? [...selectedActions, action]
                                    : selectedActions.filter(a => a !== action);
                                  handlePermissionChange(permission.resource, newActions);
                                }}
                                className="rounded border-line-strong text-blue-600 dark:text-blue-300 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{action}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
                  <span className="text-red-700 dark:text-red-300">{error}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-700 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRegisterAgent}
                  disabled={!isFormValid() || loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Registering...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Register Agent
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'manage' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-content">Registered Agents</h3>
                <span className="text-sm text-content-muted">{existingAgents.length} agents</span>
              </div>

              {existingAgents.length === 0 ? (
                <div className="text-center py-8">
                  <Key className="w-12 h-12 text-content-subtle mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-content mb-2">No Agents Registered</h3>
                  <p className="text-content-secondary mb-4">Register your first AI agent to get started.</p>
                  <button
                    onClick={() => setActiveTab('register')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Register Agent
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {existingAgents.map((agent) => (
                    <div key={agent.id} className="border border-line rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-medium text-content">{agent.name}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(agent.status)}`}>
                              {getStatusIcon(agent.status)}
                              {agent.status}
                            </span>
                          </div>
                          <p className="text-sm text-content-secondary mb-2">{agent.description}</p>
                          <div className="flex items-center gap-4 text-sm text-content-muted">
                            <span>Owner: {agent.owner}</span>
                            <span>Created: {formatDate(agent.audit.createdAt)}</span>
                            <span>Auth: {agent.credentials.type.replace('_', ' ').toUpperCase()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                        <div>
                          <p className="text-xs text-content-muted">Total Requests</p>
                          <p className="font-medium">{agent.usage.totalRequests.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-content-muted">Quota Used</p>
                          <p className="font-medium">
                            {Math.round((agent.usage.quotaUsed / agent.usage.quotaLimit) * 100)}%
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-content-muted">Last Used</p>
                          <p className="font-medium">{formatLastUsed(agent.usage.lastRequest)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-content-muted">Permissions</p>
                          <p className="font-medium">{agent.permissions.length} resources</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewAgentCredentials(agent)}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30"
                        >
                          <Eye className="w-3 h-3" />
                          View Credentials
                        </button>
                        <button
                          onClick={() => handleRegenerateCredentials(agent.id)}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-50 dark:bg-dark-700 text-content-secondary rounded hover:bg-gray-100 dark:hover:bg-dark-700"
                          disabled={loading}
                        >
                          <Key className="w-3 h-3" />
                          Regenerate
                        </button>
                        {agent.status === 'active' ? (
                          <button
                            onClick={() => handleSuspendAgent(agent.id)}
                            className="flex items-center gap-1 px-3 py-1 text-sm bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
                          >
                            <AlertCircle className="w-3 h-3" />
                            Suspend
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivateAgent(agent.id)}
                            className="flex items-center gap-1 px-3 py-1 text-sm bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-300 rounded hover:bg-green-100 dark:hover:bg-green-900/30"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Reactivate
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
                  <span className="text-red-700 dark:text-red-300">{error}</span>
                </div>
              )}
            </div>
          )}

          {activeTab === 'credentials' && (generatedCredentials || selectedAgent) && (
            <div className="space-y-6">
              {/* Header - different for new vs existing agents */}
              {generatedCredentials ? (
                <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-300" />
                  <div>
                    <p className="font-medium text-green-900 dark:text-green-300">Agent Registered Successfully!</p>
                    <p className="text-sm text-green-700 dark:text-green-300">Save these credentials securely - they won't be shown again.</p>
                  </div>
                </div>
              ) : selectedAgent && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/50 rounded-lg">
                  <Key className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-300">Viewing Credentials for: {selectedAgent.name}</p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">Authentication Type: {selectedAgent.credentials.type.replace('_', ' ').toUpperCase()}</p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Handle API Key credentials */}
                {(generatedCredentials?.apiKey || (selectedAgent?.credentials.type === 'api_key' && selectedAgent?.credentials.credentials.apiKey)) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      API Key
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type={showCredentials ? 'text' : 'password'}
                        value={generatedCredentials?.apiKey || selectedAgent?.credentials.credentials.apiKey}
                        readOnly
                        className="flex-1 px-3 py-2 border border-line-strong rounded-lg bg-gray-50 dark:bg-dark-700 font-mono text-sm"
                      />
                      <button
                        onClick={() => setShowCredentials(!showCredentials)}
                        className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {showCredentials ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(generatedCredentials?.apiKey || selectedAgent?.credentials.credentials.apiKey, 'apiKey')}
                        className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {copiedField === 'apiKey' ? (
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Handle OAuth credentials */}
                {(generatedCredentials?.clientId || (selectedAgent?.credentials.type === 'oauth' && selectedAgent?.credentials.credentials.clientId)) && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Client ID
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={generatedCredentials?.clientId || selectedAgent?.credentials.credentials.clientId}
                          readOnly
                          className="flex-1 px-3 py-2 border border-line-strong rounded-lg bg-gray-50 dark:bg-dark-700 font-mono text-sm"
                        />
                        <button
                          onClick={() => copyToClipboard(generatedCredentials?.clientId || selectedAgent?.credentials.credentials.clientId, 'clientId')}
                          className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          {copiedField === 'clientId' ? (
                            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Client Secret
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type={showCredentials ? 'text' : 'password'}
                          value={generatedCredentials?.clientSecret || selectedAgent?.credentials.credentials.clientSecret}
                          readOnly
                          className="flex-1 px-3 py-2 border border-line-strong rounded-lg bg-gray-50 dark:bg-dark-700 font-mono text-sm"
                        />
                        <button
                          onClick={() => setShowCredentials(!showCredentials)}
                          className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          {showCredentials ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => copyToClipboard(generatedCredentials?.clientSecret || selectedAgent?.credentials.credentials.clientSecret, 'clientSecret')}
                          className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          {copiedField === 'clientSecret' ? (
                            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Handle Webhook credentials */}
                {(generatedCredentials?.webhookSecret || (selectedAgent?.credentials.type === 'webhook_signature' && selectedAgent?.credentials.credentials.secret)) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Webhook Secret
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type={showCredentials ? 'text' : 'password'}
                        value={generatedCredentials?.webhookSecret || selectedAgent?.credentials.credentials.secret}
                        readOnly
                        className="flex-1 px-3 py-2 border border-line-strong rounded-lg bg-gray-50 dark:bg-dark-700 font-mono text-sm"
                      />
                      <button
                        onClick={() => setShowCredentials(!showCredentials)}
                        className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {showCredentials ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(generatedCredentials?.webhookSecret || selectedAgent?.credentials.credentials.secret, 'webhookSecret')}
                        className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {copiedField === 'webhookSecret' ? (
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {generatedCredentials?.clientId && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Client ID
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={generatedCredentials?.clientId || selectedAgent?.credentials.credentials.clientId}
                          readOnly
                          className="flex-1 px-3 py-2 border border-line-strong rounded-lg bg-gray-50 dark:bg-dark-700 font-mono text-sm"
                        />
                        <button
                          onClick={() => copyToClipboard(generatedCredentials?.clientId || selectedAgent?.credentials.credentials.clientId, 'clientId')}
                          className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          {copiedField === 'clientId' ? (
                            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Client Secret
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type={showCredentials ? 'text' : 'password'}
                          value={generatedCredentials?.clientSecret || selectedAgent?.credentials.credentials.clientSecret}
                          readOnly
                          className="flex-1 px-3 py-2 border border-line-strong rounded-lg bg-gray-50 dark:bg-dark-700 font-mono text-sm"
                        />
                        <button
                          onClick={() => setShowCredentials(!showCredentials)}
                          className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          {showCredentials ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => copyToClipboard(generatedCredentials?.clientSecret || selectedAgent?.credentials.credentials.clientSecret, 'clientSecret')}
                          className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          {copiedField === 'clientSecret' ? (
                            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {(generatedCredentials?.webhookSecret || (selectedAgent?.credentials.type === 'webhook_signature' && selectedAgent?.credentials.credentials.secret)) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Webhook Secret
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type={showCredentials ? 'text' : 'password'}
                        value={generatedCredentials?.webhookSecret || selectedAgent?.credentials.credentials.secret}
                        readOnly
                        className="flex-1 px-3 py-2 border border-line-strong rounded-lg bg-gray-50 dark:bg-dark-700 font-mono text-sm"
                      />
                      <button
                        onClick={() => setShowCredentials(!showCredentials)}
                        className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {showCredentials ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(generatedCredentials?.webhookSecret || selectedAgent?.credentials.credentials.secret, 'webhookSecret')}
                        className="p-2 text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {copiedField === 'webhookSecret' ? (
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-300 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-900 dark:text-yellow-300">Important Security Notes</p>
                    <ul className="text-sm text-yellow-800 dark:text-yellow-300 mt-2 space-y-1">
                      <li>• Store these credentials securely - they provide full API access</li>
                      <li>• Never share credentials in public repositories or logs</li>
                      <li>• Rotate credentials regularly for enhanced security</li>
                      <li>• Monitor usage and revoke access if suspicious activity is detected</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default APIKeysModal;